// PlainTextExcludeMentionPlugin.tsx
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $getRoot, ElementNode, LexicalNode, ParagraphNode, TextNode } from "lexical";
import { useEffect } from "react";
import { MentionNode } from "./MentionNode";

type Props = {
  onChange: (text: string) => void;
};

/**
 * すべてのノードタイプを処理して、テキスト内容を抽出する関数
 * - MentionNode: スキップ（空文字を返す）
 * - TextNode: テキスト内容を返す
 * - ElementNode: 子ノードを再帰的に処理
 * - ParagraphNode: 内容の後に改行を追加
 * - linebreak: 改行を返す
 * - その他のブロックレベル要素: 内容の後に改行を追加
 * - その他のノード: テキスト内容を返す
 */
function extractPlainTextExcludingMention(node: LexicalNode): string {
  // MentionNode をスキップ
  if (node instanceof MentionNode) {
    return "";
  }

  // ノードタイプを取得
  const nodeType = node.getType();

  // テキストノードの処理
  if (node instanceof TextNode) {
    return node.getTextContent();
  }

  // 改行ノードの特別な処理
  if (nodeType === "linebreak") {
    return "\n";
  }

  // ElementNode の処理
  if (node instanceof ElementNode) {
    const children = node.getChildren();

    // 子ノードの内容を結合
    const content = children
      .map(child => extractPlainTextExcludingMention(child))
      .join("");

    // 段落ノードなら改行を追加
    if (node instanceof ParagraphNode) {
      return content + "\n";
    }

    // その他のブロックレベル要素の処理
    const blockLevelTypes = [
      "list",
      "list-item",
      "quote",
      "heading",
      "code-block",
      "table",
      "table-row",
      "table-cell",
    ];

    // ブロックレベル要素なら改行を追加
    if (blockLevelTypes.some(type => nodeType === type || nodeType.includes(type))) {
      return content + (content.endsWith("\n") ? "" : "\n");
    }

    return content;
  }

  // その他のノードタイプ
  try {
    return node.getTextContent();
  } catch (e) {
    console.warn(`Failed to get text content for node type: ${nodeType}`, e);
    return "";
  }
}

export function CaptureTextChangePlugin({ onChange }: Props) {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    const removeListener = editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        const root = $getRoot();
        const plainText = extractPlainTextExcludingMention(root);
        onChange(plainText);
      });
    });
    return () => {
      removeListener();
    };
  }, [editor, onChange]);

  return null;
}
